import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { DropletsIcon, WindIcon, CarIcon, TractorIcon, PaintbrushIcon, AnchorIcon, BrickWallIcon, LayersIcon, TrainIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ServicesHubProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ServicesHub({
  setPage
}: ServicesHubProps) {
  const services = [{
    icon: DropletsIcon,
    title: 'Dustless Blasting',
    desc: 'Low-mess wet blasting that suppresses dust and cleans surfaces without damage to surrounding areas. Ideal for urban environments and sensitive substrates.',
    link: 'services' // Maps to the ServicePageTemplate
  }, {
    icon: WindIcon,
    title: 'Dry Blasting',
    desc: 'Traditional abrasive blasting for heavy-duty coating removal, severe rust, and aggressive surface preparation on robust industrial materials.',
    link: 'contact'
  }, {
    icon: CarIcon,
    title: 'Automotive Restoration',
    desc: 'Strip paint, rust, and body filler from cars, trucks, and classic vehicles down to bare metal without warping thin sheet metal panels.',
    link: 'contact'
  }, {
    icon: TractorIcon,
    title: 'Heavy Equipment Cleaning',
    desc: 'Remove rust, industrial paint, and heavy grease buildup from construction, farm, and industrial machinery to prepare for recoating.',
    link: 'contact'
  }, {
    icon: PaintbrushIcon,
    title: 'Graffiti Removal',
    desc: 'Restore brick, concrete, and stone surfaces by removing graffiti quickly and effectively without damaging the underlying substrate.',
    link: 'contact'
  }, {
    icon: AnchorIcon,
    title: 'Boat & Marine Prep',
    desc: 'Strip bottom paint, anti-fouling coatings, and gelcoat blisters for marine refinishing and fiberglass restoration.',
    link: 'contact'
  }, {
    icon: BrickWallIcon,
    title: 'Concrete & Brick Restoration',
    desc: 'Clean, etch, and restore concrete and masonry surfaces for commercial buildings, historic restorations, and residential projects.',
    link: 'contact'
  }, {
    icon: LayersIcon,
    title: 'Powder Coating Removal',
    desc: 'Efficiently strip tough, baked-on powder coating from metal parts, frames, and industrial equipment.',
    link: 'contact'
  }, {
    icon: TrainIcon,
    title: 'Rail Car Blasting',
    desc: 'Blast and prepare rail cars, tankers, and rolling stock for repainting, maintenance, and compliance inspections.',
    link: 'contact'
  }];
  return <div className="w-full bg-white" data-id="element-604">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-605">
        <div className="max-w-7xl mx-auto text-center" data-id="element-606">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-607">
            Our <span className="text-brand-red" data-id="element-608">Services</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed" data-id="element-609">
            Professional mobile surface preparation across Michigan. We utilize
            both dustless and dry blasting techniques to deliver the perfect
            finish for any project.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-steel-50" data-id="element-610">
        <div className="max-w-7xl mx-auto px-4" data-id="element-611">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-612">
            {services.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 border border-steel-200 hover:border-brand-red hover:shadow-xl transition-all duration-300 group flex flex-col h-full" data-id="element-613">
                <div className="w-14 h-14 bg-steel-50 rounded-full flex items-center justify-center mb-6 border border-steel-100 group-hover:bg-brand-red/10 group-hover:border-brand-red/30 transition-colors" data-id="element-614">
                  <service.icon className="w-7 h-7 text-brand-red group-hover:scale-110 transition-transform duration-300" data-id="element-615" />
                </div>
                <h3 className="text-2xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-616">
                  {service.title}
                </h3>
                <p className="text-steel-600 mb-8 text-base leading-relaxed flex-grow" data-id="element-617">
                  {service.desc}
                </p>
                <button onClick={() => setPage(service.link)} className="inline-flex items-center text-brand-red font-bold text-sm uppercase tracking-wider hover:text-brand-red-dark mt-auto self-start" data-id="element-618">
                  Learn More{' '}
                  <span className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-619">
                    →
                  </span>
                </button>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-charcoal text-white text-center px-4" data-id="element-620">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-621">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-622">
            Not Sure What You Need?
          </h2>
          <p className="text-xl font-body mb-10 text-steel-300" data-id="element-623">
            Every project is unique. Contact us to discuss your specific surface
            preparation requirements and we'll recommend the best approach.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-624">
            <Button variant="primary" size="lg" onClick={() => setPage('contact')} showPhoneIcon className="w-full sm:w-auto" data-id="element-625">
              Call (517) 777-8088
            </Button>
            <Button variant="outline" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto" data-id="element-626">
              Request a Quote
            </Button>
          </div>
        </motion.div>
      </section>
    </div>;
}